import React, { createContext, memo, useContext, useMemo } from "react";
import {
   Div,
   Icon,
   lightenColor,
   PageHolder,
   Text,
   useBetterHtmlContext,
   useMediaQuery,
   useTheme,
} from "react-better-html";

import { centerContentPageHolderDecorationWidth } from "../../constants/app";

import { type OrganizationType } from "../../types/organization";

import AppLogo from "../AppLogo";
import DecorationPropertyCard from "../decorations/DecorationPropertyCard";
import DecorationTotalTrackedCard from "../decorations/DecorationTotalTrackedCard";
import DecorationPriceTrendCard from "../decorations/DecorationPriceTrendCard";
import DecorationFamilyCard from "../decorations/DecorationFamilyCard";
import DecorationMembersListCard from "../decorations/DecorationMembersListCard";
import DecorationOrganizationNameCard from "../decorations/DecorationOrganizationNameCard";

type CenterContentPageHolderContext = {
   withDecoration: boolean;
   gap: number;
};

const centerContentPageHolderContext = createContext<CenterContentPageHolderContext | undefined>(undefined);

const useCenterContentPageHolderContext = () => {
   const context = useContext(centerContentPageHolderContext);

   if (!context) {
      throw new Error("`useCenterContentPageHolderContext()` must be used within `<CenterContentPageHolder>`");
   }

   return context;
};

export type CenterContentPageHolderProps = {
   withDecoration?: boolean;
   children?: React.ReactNode;
};

type CenterContentPageHolderComponentType = {
   (props: CenterContentPageHolderProps): React.ReactElement;
   content: (props: CenterContentPageHolderComponentContentProps) => React.ReactElement;
   decoration: (props: CenterContentPageHolderComponentDecorationProps) => React.ReactElement;
};

const CenterContentPageHolderComponent: CenterContentPageHolderComponentType = function CenterContentPageHolder({
   withDecoration,
   children,
}: CenterContentPageHolderProps) {
   const theme = useTheme();
   const mediaQuery = useMediaQuery();
   const { app } = useBetterHtmlContext();

   const gap = theme.styles.space * 3;
   const readyWithDecoration = !mediaQuery.size800 && withDecoration;

   const value = useMemo<CenterContentPageHolderContext>(
      () => ({
         withDecoration: readyWithDecoration ?? false,
         gap,
      }),
      [readyWithDecoration, gap],
   );

   return (
      <centerContentPageHolderContext.Provider value={value}>
         <PageHolder.center
            contentMaxWidth={app.contentMaxWidth / 2 + (withDecoration ? centerContentPageHolderDecorationWidth : 0)}
            pageBackgroundColor={theme.colors.backgroundThree}
            border="none"
            borderRadius={theme.styles.borderRadius * 2}
            boxShadow={theme.styles.boxShadow}
            padding={theme.styles.space * (mediaQuery.size600 ? 2 : 3)}
         >
            <Div className="center-content-page-holder-root" />

            {withDecoration ? (
               <Div.row width="100%" gap={gap}>
                  {children}
               </Div.row>
            ) : (
               children
            )}
         </PageHolder.center>
      </centerContentPageHolderContext.Provider>
   );
} as any;

type CenterContentPageHolderComponentContentProps = {
   children?: React.ReactNode;
};

CenterContentPageHolderComponent.content = function Content({ children }) {
   const { withDecoration, gap } = useCenterContentPageHolderContext();

   return (
      <Div width={`calc(100% - ${withDecoration ? centerContentPageHolderDecorationWidth + gap : 0}px)`} flexShrink={0}>
         {children}
      </Div>
   );
} as CenterContentPageHolderComponentType["content"];

export type CenterContentPageHolderComponentDecorationProps = {
   variant?: "option1" | "option2" | "organizationName" | "organizationMembers";
   organizationName?: string;
   organizationType?: OrganizationType;
   members?: string[];
   marketingText?: string;
};

CenterContentPageHolderComponent.decoration = function Decoration({
   variant = "option1",
   marketingText,
   organizationName,
   organizationType,
   members,
}) {
   const theme = useTheme();
   const { withDecoration, gap } = useCenterContentPageHolderContext();

   const blobColor = lightenColor(theme.colors.primary, 0.1);

   return withDecoration ? (
      <Div
         position="relative"
         width={centerContentPageHolderDecorationWidth + gap}
         minHeight={480}
         flexShrink={0}
         backgroundImage={`linear-gradient(to top, ${theme.colors.secondary}, ${theme.colors.primary})`}
         borderTopRightRadius={theme.styles.borderRadius * 2}
         borderBottomRightRadius={theme.styles.borderRadius * 2}
         overflow="hidden"
         marginBlock={-gap}
         padding={gap}
      >
         <Icon name="blob01" position="absolute" color={blobColor} size={260} top={-20} right={-100} />
         <Icon name="blob02" position="absolute" color={blobColor} size={260} bottom={-20} left={-100} />

         <AppLogo mode="dark" marginBottom={theme.styles.space * 2} />

         {variant === "option1" ? (
            <>
               <DecorationPropertyCard
                  position="absolute"
                  top="40%"
                  left="45%"
                  transform="translate(-50%, -50%) rotate(-3deg)"
               />
               <DecorationTotalTrackedCard
                  position="absolute"
                  top="38%"
                  left="75%"
                  transform="translate(-50%, -50%) rotate(5deg)"
               />
               <DecorationPriceTrendCard
                  position="absolute"
                  top="70%"
                  left="45%"
                  transform="translate(-50%, -50%) rotate(-1deg)"
               />
            </>
         ) : variant === "option2" ? (
            <>
               <DecorationFamilyCard
                  position="absolute"
                  top="35%"
                  left="40%"
                  transform="translate(-50%, -50%) rotate(-3deg)"
               />
               <DecorationMembersListCard
                  position="absolute"
                  top="65%"
                  left="65%"
                  transform="translate(-50%, -50%) rotate(5deg)"
               />
            </>
         ) : variant === "organizationName" ? (
            <>
               <DecorationOrganizationNameCard
                  organizationName={organizationName}
                  organizationType={organizationType}
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%) rotate(-2deg)"
               />
            </>
         ) : variant === "organizationMembers" ? (
            <>
               <DecorationMembersListCard
                  members={members}
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%) rotate(-2deg)"
               />
            </>
         ) : undefined}

         {marketingText && (
            <Text
               position="absolute"
               width="100%"
               bottom={gap}
               left="50%"
               fontSize={14}
               textAlign="center"
               color={theme.colors.base + "a0"}
               transform="translateX(-50%)"
               paddingInline={gap}
            >
               {marketingText}
            </Text>
         )}
      </Div>
   ) : (
      <></>
   );
} as CenterContentPageHolderComponentType["decoration"];

const CenterContentPageHolder = memo(
   CenterContentPageHolderComponent,
) as any as typeof CenterContentPageHolderComponent & {
   content: typeof CenterContentPageHolderComponent.content;
   decoration: typeof CenterContentPageHolderComponent.decoration;
};

CenterContentPageHolder.content = CenterContentPageHolderComponent.content;
CenterContentPageHolder.decoration = CenterContentPageHolderComponent.decoration;

export default CenterContentPageHolder;
